import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession } from '../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../app/Helpers';
import { TableState } from '../../../common/Table/TableSate';
import { GetPrivilegesRequest, GetPrivilegesResponse } from '../../../../repository/UserManagement/Customers/privilege_pb';
import { number } from 'yup';
import { PrivilegeEndpointClient } from '../../../../repository/UserManagement/Customers/PrivilegeServiceClientPb';
import { AddPrivilegesRequest, AddPrivilegesResponse } from '../../../../repository/UserManagement/Customers/privilege_pb';


export interface PrivilegesFormState {
    privileges: TableState
    message?: ApiMessage,
    searchText: string | undefined,
    filters: any[],
    isLoading: boolean,

}

const initialState: PrivilegesFormState = {
    privileges: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 500, selected: [], isFilteredSet: false },
    message: undefined,
    searchText: undefined,
    filters: [],
    isLoading: false,

}

const clientPrivilege = new PrivilegeEndpointClient(API_URL, null, null);


export const getPrivileges = createAsyncThunk<GetPrivilegesResponse.AsObject, APIRequest<GetPrivilegesRequest>, {

    rejectValue: ApiMessage
}>(
    'forms/customers/privileges/getAvailable',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {

                var r = await clientPrivilege.getPrivileges(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }


        }
        return await callReq();
    }

)


export const add = createAsyncThunk<AddPrivilegesResponse.AsObject, APIRequest<AddPrivilegesRequest>, {

    rejectValue: ApiMessage
}>(
    'forms/customers/privileges/add',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await clientPrivilege.add(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }


        }
        return await callReq();
    }

)
const getFilters = (state: PrivilegesFormState): any[] => {

    return [];

}
export const customersPrivilegesFromSlice = createSlice({
    name: 'forms/customers/privileges',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.privileges = initialState.privileges;
            state.searchText = initialState.searchText;
        },
        resetPrivileges: (state) => {
            state.privileges.items = [];
            state.privileges.hasMore = true;
        },

        setSort: (state, action: PayloadAction<boolean>) => {
            state.privileges.isDescending = action.payload;
        },
        setNumberOfResults: (state, action: PayloadAction<number>) => {
            state.privileges.numberOfResults = action.payload;
        },
        setSelectedPrivilege: (state, action: PayloadAction<any[]>) => {
            state.privileges.selected = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.searchText = action.payload;
        },
        setIsFilteredSet: (state, action: PayloadAction<boolean>) => {
            state.privileges.isFilteredSet = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getPrivileges.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.privilegesList.map(val => {
                    return {
                        id: val.id,
                        name: val.name?.value ?? "",
                        keyName: val.keyname?.value ?? "",
                        isAvailable: val.isavailable?.value,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.addedby?.firstname?.value ?? "") + " " + (val.addedby?.lastname?.value ?? ""),
                        addedById: val.addedby?.id?.value,
                    }
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                let l = [];
                if (state.privileges.items.length != 0) {
                    l = state.privileges.items.slice(0, state.privileges.items.length - 10)
                }
                l = l.concat(r);
                if (r.length < state.privileges.numberOfResults) {
                    state.privileges.hasMore = false;
                } else {
                    l = l.concat(...Array(10).fill(null));
                }

                state.privileges.items = l
                //console.log(state.items)

            } else {
                state.privileges.hasMore = false;
            }
            state.privileges.isFetching = false

        })
        builder.addCase(getPrivileges.rejected, (state, action) => {
            state.privileges.isFetching = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getPrivileges.pending, (state, action) => {
            state.privileges.isFetching = true;
            state.message = undefined;
        })

        builder.addCase(add.fulfilled, (state, { payload }) => {
            state.isLoading = false;
            if (payload)
                state.message = toApiMessage(payload.success?.message);

        })
        builder.addCase(add.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(add.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })
    }
})

export const { reset, dismissMessage, setSort, setNumberOfResults, resetPrivileges, setSelectedPrivilege, setSearchText, setIsFilteredSet } = customersPrivilegesFromSlice.actions

export default customersPrivilegesFromSlice.reducer


